/**
 * Coerces persisted structured metadata into safe shapes for UI rendering.
 * Documents may carry legacy or malformed array fields (e.g. keyParties as a string).
 */

export type StructuredKeyDate = { label: string; date: string };
export type StructuredKeyAmount = {
  label: string;
  amount: number;
  context?: string;
};
export type StructuredKeyParty = { name: string; role: string };
export type StructuredFlag = {
  flag: string;
  severity: "info" | "warning" | "critical";
  detail: string;
};
export type StructuredEntities = {
  primaryEntity?: string;
  relatedEntities?: string[];
};

export interface NormalizedStructuredMetadata {
  summary?: string;
  keyDates: StructuredKeyDate[];
  keyAmounts: StructuredKeyAmount[];
  keyParties: StructuredKeyParty[];
  entities: StructuredEntities;
  flags: StructuredFlag[];
  metadata?: Record<string, string | number | boolean | null>;
  confidence?: number;
}

export function isNewMetadataShape(meta: unknown): boolean {
  return (
    typeof meta === "object" &&
    meta !== null &&
    "summary" in (meta as Record<string, unknown>)
  );
}

export function normalizeKeyDates(value: unknown): StructuredKeyDate[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter(
      (item): item is Record<string, unknown> =>
        typeof item === "object" && item !== null,
    )
    .filter(
      (item) => typeof item.label === "string" && typeof item.date === "string",
    )
    .map((item) => ({
      label: item.label as string,
      date: item.date as string,
    }));
}

export function normalizeKeyAmounts(value: unknown): StructuredKeyAmount[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter(
      (item): item is Record<string, unknown> =>
        typeof item === "object" && item !== null,
    )
    .filter(
      (item) =>
        typeof item.label === "string" && typeof item.amount === "number",
    )
    .map((item) => ({
      label: item.label as string,
      amount: item.amount as number,
      ...(typeof item.context === "string" ? { context: item.context } : {}),
    }));
}

export function normalizeKeyParties(value: unknown): StructuredKeyParty[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter(
      (item): item is Record<string, unknown> =>
        typeof item === "object" && item !== null,
    )
    .filter(
      (item) => typeof item.name === "string" && typeof item.role === "string",
    )
    .map((item) => ({
      name: item.name as string,
      role: item.role as string,
    }));
}

export function normalizeEntities(value: unknown): StructuredEntities {
  if (typeof value !== "object" || value === null) return {};

  const obj = value as Record<string, unknown>;
  const result: StructuredEntities = {};

  if (typeof obj.primaryEntity === "string" && obj.primaryEntity) {
    result.primaryEntity = obj.primaryEntity;
  }

  if (Array.isArray(obj.relatedEntities)) {
    const filtered = obj.relatedEntities.filter(
      (e): e is string => typeof e === "string",
    );
    if (filtered.length > 0) {
      result.relatedEntities = filtered;
    }
  }

  return result;
}

export function normalizeFlags(value: unknown): StructuredFlag[] {
  if (!Array.isArray(value)) return [];

  const validSeverities = new Set(["info", "warning", "critical"]);

  return value
    .filter(
      (item): item is Record<string, unknown> =>
        typeof item === "object" && item !== null,
    )
    .filter(
      (item) =>
        typeof item.flag === "string" &&
        typeof item.severity === "string" &&
        validSeverities.has(item.severity) &&
        typeof item.detail === "string",
    )
    .map((item) => ({
      flag: item.flag as string,
      severity: item.severity as StructuredFlag["severity"],
      detail: item.detail as string,
    }));
}

/** Normalizes array fields on structured metadata read from Convex. */
export function coerceStructuredMetadata(
  meta: unknown,
): NormalizedStructuredMetadata | null {
  if (!isNewMetadataShape(meta)) return null;

  const raw = meta as Record<string, unknown>;
  const metadata =
    typeof raw.metadata === "object" &&
    raw.metadata !== null &&
    !Array.isArray(raw.metadata)
      ? (raw.metadata as Record<string, string | number | boolean | null>)
      : undefined;

  return {
    summary: typeof raw.summary === "string" ? raw.summary : undefined,
    keyDates: normalizeKeyDates(raw.keyDates),
    keyAmounts: normalizeKeyAmounts(raw.keyAmounts),
    keyParties: normalizeKeyParties(raw.keyParties),
    entities: normalizeEntities(raw.entities),
    flags: normalizeFlags(raw.flags),
    metadata,
    confidence: typeof raw.confidence === "number" ? raw.confidence : undefined,
  };
}
